import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './styles/customTest.css';

/**
 * 单个测试场景组件
 * 
 * @param {Object} scenario - 当前场景数据
 * @param {Function} onNext - 下一步回调函数
 * @param {Function} onPrevious - 上一步回调函数
 * @param {Function} onSaveResponse - 保存用户回答的回调函数
 * @param {boolean} isFirst - 是否为第一个场景
 * @param {boolean} isLast - 是否为最后一个场景
 * @param {number} currentStep - 当前步骤
 * @param {number} totalSteps - 总步骤数
 * @param {boolean} isSubmitting - 是否正在提交数据
 */
function TestScenario({ 
  scenario, 
  onNext, 
  onPrevious, 
  onSaveResponse, 
  isFirst, 
  isLast, 
  currentStep, 
  totalSteps,
  isSubmitting = false // 添加默认值
}) {
  const [response, setResponse] = useState('');
  const [isValid, setIsValid] = useState(true);
  const maxResponseLength = 500; // 最大500字符限制
  
  // 加载保存的回答（如果有）
  useEffect(() => {
    const savedResponse = localStorage.getItem(`scenario_response_${scenario.id}`);
    if (savedResponse) {
      setResponse(savedResponse);
    } else {
      setResponse('');
    }
  }, [scenario.id]);
  
  // 固定的图片映射
  const getScenarioImage = (scenarioId) => {
    const imageMap = {
      'presentation-proposal': 'presentation.jpg',
      'team-conflict': 'team-conflict.jpg',
      'networking-event': 'networking.jpg',
      'difficult-feedback': 'feedback.jpg',
      'job-interview': 'interview.jpg',
      'public-speaking': 'public-speaking.jpg',
      'difficult-client': 'public-speaking.jpg', // 重用公共演讲图片
      'social-event': 'networking.jpg', // 重用社交场景图片
      'team-motivation': 'team-conflict.jpg', // 重用团队场景图片
      'group-discussion': 'presentation.jpg', // 重用演示场景图片
      'friendship-boundaries': 'feedback.jpg', // 重用反馈场景图片
      'salary-negotiation': 'interview.jpg' // 重用面试场景图片
    };
    
    return imageMap[scenarioId] || 'presentation.jpg'; // 默认使用演示图片
  }
  
  const handleResponseChange = (e) => {
    const value = e.target.value;
    // 限制输入不超过最大字符数
    if (value.length <= maxResponseLength) {
      setResponse(value);
      setIsValid(true);
    }
  };
  
  const handleNext = () => {
    onSaveResponse(scenario.id, response);
    localStorage.setItem(`scenario_response_${scenario.id}`, response);
    onNext();
  };
  
  const handlePrevious = () => {
    onSaveResponse(scenario.id, response);
    localStorage.setItem(`scenario_response_${scenario.id}`, response);
    onPrevious();
  };
  
  return (
    <div className="scenario-container">
      <div className="scenario-progress">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${(currentStep / (totalSteps - 1)) * 100}%` }}
          ></div>
        </div>
        <div className="step-info">
          Scenario {currentStep + 1} / {totalSteps}
        </div>
      </div>
      
      <div className="scenario-header">
        <h2>{scenario.title}</h2>
      </div>
      
      <div className="scenario-content">
        <div className="scenario-image">
          <img 
            src={`/assets/images/scenarios/${getScenarioImage(scenario.id)}`} 
            alt={scenario.title} 
            onError={(e) => {
              console.error(`图片加载失败: ${scenario.id}`);
              e.target.onerror = null;
              e.target.src = '/assets/images/scenarios/presentation.jpg';
            }}
          />
        </div>
        
        <div className="scenario-description">
          <p>{scenario.description}</p>
        </div>
      </div>
      
      <div className="scenario-response">
        <h3>How would you handle this situation?</h3>
        <textarea
          value={response}
          onChange={handleResponseChange}
          placeholder="Please enter your response here..."
          rows={8}
          className={isValid ? '' : 'invalid'}
        ></textarea>
        <div className="character-count">
          Characters: {response.length} (maximum {maxResponseLength})
        </div>
      </div>
      
      <div className="scenario-navigation">
        {!isFirst && (
          <button 
            className="btn-previous" 
            onClick={handlePrevious}
            disabled={isSubmitting} // 禁用按钮
          >
            <span className="arrow">←</span> Previous
          </button>
        )}
        
        <button 
          className={`btn-${isLast ? 'finish' : 'next'}`} 
          onClick={handleNext}
          disabled={isSubmitting} // 禁用按钮
        >
          {isLast ? (isSubmitting ? 'Saving...' : 'Complete Test') : 'Next'} {!isLast && <span className="arrow">→</span>}
        </button>
      </div>
    </div>
  );
}

TestScenario.propTypes = {
  scenario: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    imagePlaceholder: PropTypes.string
  }).isRequired,
  onNext: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
  onSaveResponse: PropTypes.func.isRequired,
  isFirst: PropTypes.bool.isRequired,
  isLast: PropTypes.bool.isRequired,
  currentStep: PropTypes.number.isRequired,
  totalSteps: PropTypes.number.isRequired,
  isSubmitting: PropTypes.bool // 添加PropTypes定义
};

export default TestScenario; 